import type { CleaningConfirmationPending } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip'
import { tentativeExplanation } from './presentation'

/**
 * The Tentative badge, with a tooltip saying why and how long until it
 * confirms (CAP-794).
 *
 * The explanation is worked out when the tooltip opens rather than when the
 * row renders, so a board left open all afternoon still counts down from the
 * moment it is read. A job admin-api sent nothing to explain gets the plain
 * badge, without a tooltip that would only say "tentative" again.
 */
export function TentativeBadge({
  pending,
  timeZone,
  className,
}: {
  pending: CleaningConfirmationPending | null | undefined
  timeZone: string
  className?: string
}) {
  const label = tentativeExplanation(pending, timeZone)
  if (!label) {
    return (
      <Badge variant="secondary" className={className}>
        Tentative
      </Badge>
    )
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="secondary" className={`cursor-help ${className ?? ''}`} tabIndex={0}>
          Tentative
        </Badge>
      </TooltipTrigger>
      {/* Portalled: a tentative row is drawn faded, and content rendered
          inside it would inherit that opacity and read as disabled. */}
      <TooltipPortal>
        <TooltipContent className="max-w-xs">
          <Explanation pending={pending} timeZone={timeZone} />
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  )
}

function Explanation({
  pending,
  timeZone,
}: {
  pending: CleaningConfirmationPending | null | undefined
  timeZone: string
}) {
  return <p>{tentativeExplanation(pending, timeZone)}</p>
}
